/**
 * Patient Create Page
 *
 * This page renders a breadcrumb navigation and the PatientCreateForm component.
 *
 * The orderId param is optional and is used to determine if the order is being created or edited.
 */
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { USAEPayPatientPaymentForm } from "./-patient-payment/usaepay-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useCallback, useState } from "react";
import ConfirmModal from "@/components/modals/confirm-modal";
import { PatientCrumbs } from "../../-crumbs";
import { PatientPaymentLinkModal } from "@/components/modals/patient-payment-link-modal";
import { PaymentProcessorEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { getGetPatientQueryOptions } from "@/requests/gen/react-query/patient";
import { StripePatientPaymentForm } from "./-patient-payment/stripe-form";
import { PriorityPatientPaymentForm } from "./-patient-payment/priority-form";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";

export const Route = createFileRoute(
  "/_private/_patient-360/patient/$patientId/create/payment",
)({
  loader: ({ context: { queryClient }, params: { patientId } }) =>
    queryClient.ensureQueryData(getGetPatientQueryOptions(patientId)),
  component: PatientPaymentCreatePage,
});

function PatientPaymentCreatePage() {
  const { patientId } = Route.useParams();
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isPaymentLinkOpen, setIsPaymentLinkOpen] = useState(false);
  const navigate = useNavigate();

  const onSuccessFn = useCallback(() => {
    setIsSuccessOpen(true);
  }, []);

  const patient = useSuspenseQuery(
    suspenseOptions(getGetPatientQueryOptions(patientId)),
  );

  const processor = patient.data?.user_entities.at(0)?.entity.payment_processor;
  const isStripe = processor === PaymentProcessorEnum.Stripe;
  const isPriority = processor === PaymentProcessorEnum.Priority;
  const isUSAePay = !isStripe && !isPriority;

  const skipBtn = (
    <Button
      onClick={(e) => {
        e.preventDefault();
        setIsSuccessOpen(true);
      }}
      variant="outline"
    >
      Skip
    </Button>
  );

  return (
    <div>
      <title>Patient Payment - Legrande Health Portal</title>
      {/* Success Modal */}
      <ConfirmModal
        isOpen={isSuccessOpen}
        text="Patient successfully created!"
        title="Success!"
        onCancel={() => {
          navigate({ to: "/order/lookup" });
        }}
        onConfirm={() => {
          // Navigate with new patient info to autofill order create form
          navigate({
            to: "/order/create",
            search: { patientId },
          });
        }}
        img="success"
        cancelText="Go to Dashboard"
        confirmText="Create New Order"
      />

      {isUSAePay && (
        <PatientPaymentLinkModal
          patientId={patientId}
          isOpen={isPaymentLinkOpen}
          setIsOpen={setIsPaymentLinkOpen}
          onSuccess={onSuccessFn}
        />
      )}

      <PatientCrumbs />

      <Card>
        <CardHeader>
          <CardTitle>New Patient Payment</CardTitle>
          <CardDescription>
            Enter patient payment details below.
          </CardDescription>
          {isUSAePay && !patient.isLoading && (
            <CardAction>
              <Button
                variant="outline"
                onClick={() => setIsPaymentLinkOpen(true)}
              >
                Text Payment Form
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {!patient.isLoading && (
            <>
              {isStripe && (
                <StripePatientPaymentForm
                  patientId={patientId}
                  onSuccessFn={onSuccessFn}
                  onCancelBtn={skipBtn}
                />
              )}
              {isPriority && !isSuccessOpen && (
                <PriorityPatientPaymentForm
                  patientId={patientId}
                  onSuccessFn={onSuccessFn}
                  onCancelBtn={skipBtn}
                />
              )}
              {isUSAePay && (
                <USAEPayPatientPaymentForm
                  patientId={patientId}
                  onSuccessFn={onSuccessFn}
                  onCancelBtn={skipBtn}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
